import React, { createContext, useContext, useState, useCallback } from "react";
import { json } from "stream/consumers";
import { createUserTask, BASE_URL } from "../Services/APIService";

// Types
export interface UserTask {
  id: number;
  tenantId: number;
  leadId: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  isPostponed: boolean;
  postponeReason: string;
  rescheduleDate: string;
  reminderDate: string;
  isCompleted: boolean;
  isCanceled: boolean;
  taskCompletionDate: string;
  projectId: number;
  projectName: string;
  followupTypeName: string;
  leadFirstName: string;
  leadLastName: string;
  leadEmail: string;
  leadMobile: string;
  createdDate: string;
  modifiedDate: string;
}

export interface UserTaskContextParams {
  tenantId: number;
  leadId?: number;
  assignedUser?: number;
  isCompleted?: boolean;
  followupDate?: string;
}

interface UserTaskContextType {
  tasks: UserTask[];
  isLoading: boolean;
  error: string | null;
  isModalOpen: boolean;
  selectedTask: UserTask | null;
  fetchTasks: (params: UserTaskContextParams) => Promise<void>;
  openModal: (task?: UserTask) => void;
  closeModal: () => void;
  updateTask: (
    taskId: number,
    updates: Partial<UserTask>,
    followupData?: CreateUserTaskRequest | null
  ) => Promise<void>;
  refreshTasks: () => Promise<void>;
}

interface CreateUserTaskRequest {
  leadId: number;
  logId: number;
  projectId: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  tenantId: number;
  createdBy: number;
}

const UserTaskContext = createContext<UserTaskContextType | undefined>(
  undefined
);

export const useUserTaskContext = (): UserTaskContextType => {
  const context = useContext(UserTaskContext);
  if (!context) {
    throw new Error(
      "useUserTaskContext must be used within a UserTaskProvider"
    );
  }
  return context;
};

interface UserTaskProviderProps {
  children: React.ReactNode;
}

export const UserTaskProvider: React.FC<UserTaskProviderProps> = ({
  children,
}) => {
  const [tasks, setTasks] = useState<UserTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<UserTask | null>(null);
  const [currentParams, setCurrentParams] =
    useState<UserTaskContextParams | null>(null);

  const fetchTasks = useCallback(async (params: UserTaskContextParams) => {
    if (!params.tenantId) {
      setError("Tenant ID is required");
      return;
    }

    setIsLoading(true);
    setError(null);
    setCurrentParams(params);

    try {
      const accessToken = localStorage.getItem("authToken");
      if (!accessToken) {
        throw new Error("No authentication token found");
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("tenantId", params.tenantId.toString());

      if (params.leadId !== undefined) {
        queryParams.append("leadId", params.leadId.toString());
      }
      if (params.assignedUser !== undefined) {
        queryParams.append("assignedUser", params.assignedUser.toString());
      }
      if (params.isCompleted !== undefined) {
        queryParams.append("isCompleted", params.isCompleted.toString());
      }
      if (params.followupDate) {
        queryParams.append("followupDate", params.followupDate);
      }

      const requestdata = JSON.stringify({
        tenantId: params.tenantId,
        leadId: params.leadId || 0,
        assignedUser: params.assignedUser || 0,
        isCompleted: params.isCompleted || false,
        followupDate: params.followupDate || "",
      });

      const response = await fetch(`${BASE_URL}/LeadAction/GetUserTasks`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: requestdata,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTasks(Array.isArray(data.tasks) ? data.tasks : []);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch tasks";
      setError(errorMessage);
      console.error("Error fetching tasks:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshTasks = useCallback(async () => {
    if (currentParams) {
      await fetchTasks(currentParams);
    }
  }, [fetchTasks, currentParams]);

  const openModal = useCallback((task?: UserTask) => {
    setSelectedTask(task || null);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTask(null);
  }, []);

  const updateTask = useCallback(
    async (
      taskId: number,
      updates: Partial<UserTask>,
      followupData?: CreateUserTaskRequest | null
    ) => {
      try {
        const accessToken = localStorage.getItem("authToken");
        const userId = parseInt(localStorage.getItem("UserId") || "0", 10);
        const tenantId = parseInt(
          localStorage.getItem("tenantId") || "1000",
          10
        ); // if available

        if (!accessToken) {
          throw new Error("No authentication token found");
        }

        console.log("Updating task with ID:", taskId, "Updates:", updates);

        let url = "";
        let requestBody: any = {};

        if (updates.isCompleted) {
          // If task is marked complete, call the CompleteUserTask API

          url = `${BASE_URL}/LeadAction/CompleteUserTask`;

          requestBody = {
            taskId: taskId,
            tenantId: tenantId,
            modifiedBy: userId,
            logNote: updates.taskNotes || "", // Optional field
          };
        } else {
          // Otherwise call the regular UpdateUserTask API
          url = `${BASE_URL}/LeadAction/UpdateUserTask`;

          requestBody = {
            id: taskId,
            ...updates,
            modifiedBy: userId,
          };
        }
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        //Mapping data with CreateUserTaskRequest.
        const requestData: CreateUserTaskRequest = {
          leadId: updates.leadId || 0,
          logId: 0,
          projectId: updates.projectId || 0,
          assignedUser: updates.assignedUser || userId,
          followupTypeId: updates.followupTypeId || 0,
          followupDate: updates.rescheduleDate || "",
          taskNotes: updates.postponeReason || "",
          tenantId: tenantId,
          createdBy: userId,
        };

        // if the postponed task is created successfully
        if (response.ok && updates.isPostponed) {
          await createUserTask(requestData);
        }

        // Handle canceled task follow-up creation
        if (response.ok && updates.isCanceled && followupData) {
          console.log("Follow-up data provided:", followupData);
          const requestData: CreateUserTaskRequest = {
            leadId: followupData.leadId || 0,
            logId: 0,
            projectId: followupData.projectId || 0,
            assignedUser: followupData.assignedUser || userId,
            followupTypeId: followupData.followupTypeId || 0,
            followupDate: followupData.followupDate || "",
            taskNotes: followupData.taskNotes || "",
            tenantId: tenantId,
            createdBy: userId,
          };
          await createUserTask(requestData);
        }

        const result = await response.json();

        // Update the task in the local state
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.id === taskId ? { ...task, ...updates } : task
          )
        );

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update task";
        setError(errorMessage);
        console.error("Error updating task:", err);
        throw err;
      }
    },
    []
  );

  const value: UserTaskContextType = {
    tasks,
    isLoading,
    error,
    isModalOpen,
    selectedTask,
    fetchTasks,
    openModal,
    closeModal,
    updateTask,
    refreshTasks,
  };

  return (
    <UserTaskContext.Provider value={value}>
      {children}
    </UserTaskContext.Provider>
  );
};
